import { useMemo } from 'react';
import { EStatusAppointment, TNullable, TStatusCamel } from '@/types';

interface IStatusBasicInfo {
  lastStatus: TNullable<TStatusCamel>;
  rescheduledDate: TNullable<{ start: string; end: string }>;
}

/**
 * Custom hook to get basic appointment status information.
 * @param statuses - Array of status objects representing the appointment's status history.
 * @returns Object containing lastStatus and rescheduledDate (if rescheduled).
 */
export const useInfoByStatus = (
  statuses: TStatusCamel[] = [],
): IStatusBasicInfo => {
  return useMemo(() => {
    const lastStatus = statuses.at(-1);

    if (!lastStatus) {
      return {
        lastStatus: null,
        rescheduledDate: null,
      };
    }

    const isPending =
      lastStatus.action === EStatusAppointment.PENDING_CONFIRMATION;
    const hasRescheduledDates =
      !!lastStatus.meta?.start && !!lastStatus.meta?.end;

    const rescheduledDate =
      isPending && hasRescheduledDates
        ? {
            start: lastStatus?.meta?.start || '',
            end: lastStatus?.meta?.end || '',
          }
        : null;

    return {
      lastStatus,
      rescheduledDate,
    };
  }, [statuses]);
};
